/**
 * Notification centre — querying, grouping, labelling and the preference matrix.
 *
 * Everything here is pure, so the parts that decide WHAT is shown can be tested
 * without a DOM or a server. Rendering stays in the view. Same split as
 * lib/activity.mjs, and day boundaries come from the same dates.mjs so the
 * notification history and the activity log agree about when "today" started
 * rather than each deriving it from its own copy of the offset.
 *
 * ── THE CATALOGUE IS NOT DUPLICATED HERE ─────────────────────────────────────
 * Labels, descriptions, groups and audience live in ONE place —
 * server/config/notificationEvents.js — and arrive with GET /preferences. A
 * mirror in this file would be a second list to keep in step, and the one that
 * silently drifted would be the one deciding what a user sees. What this module
 * holds is only what the server has no opinion about: how a severity is
 * coloured, and how a matrix of switches is turned into a PUT body.
 */
import { DAY_MS, estDateKey, estTime } from './dates.mjs';

export const NOTIF_LIMIT = 25;

/**
 * Severity presentation. Deliberately the only catalogue-ish thing defined
 * client-side: severity is a fixed, closed set of four, and colour is a purely
 * visual decision the server should not be carrying.
 *
 * All four icons are emoji, and info's ℹ️ is the reason to say so. It was a
 * bullet — a 4px dot in a column sized for the 14px glyphs beside it, so the
 * commonest severity was the one that looked like the icon had failed to load,
 * and a list of mostly-info rows read as a broken column with three coloured
 * exceptions. Anything added here should be a real glyph of the same weight.
 */
export const SEVERITY_META = {
  critical: { icon: '⛔', label: 'Critical', color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.32)' },
  warning:  { icon: '⚠️', label: 'Warning',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.32)' },
  success:  { icon: '✅', label: 'Good',     color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.32)' },
  info:     { icon: 'ℹ️', label: 'Info',     color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.32)' },
};

/**
 * The fallback for a severity this client does not recognise.
 *
 * Reads as Info rather than as an error: an unrecognised severity means the
 * server sent something this build has not been taught, which is a deployment
 * detail the person reading their notifications can neither act on nor care
 * about. A ❓ or a broken glyph would put that in front of them instead of the
 * alert they opened the page for. The neutral grey is what distinguishes it —
 * quietly, and only to someone looking for it.
 */
const UNKNOWN_SEVERITY = { icon: 'ℹ️', label: 'Info', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.3)' };

/** Never returns undefined — a row with an unrecognised severity still renders. */
export function severityMeta(severity) {
  return SEVERITY_META[severity] ?? UNKNOWN_SEVERITY;
}

/**
 * Which toast variant class a severity gets.
 *
 * react-hot-toast emits no data-attribute for a toast type, so a class is the
 * only hook it gives you — the same constraint App.jsx works around for its
 * error/success toasts. Four severities collapse to three edges because `info`
 * is the unstyled default, and an unknown severity falls through to it rather
 * than rendering an unstyled-but-differently-broken toast.
 */
export function severityToastClass(severity) {
  return {
    critical: 'app-toast--error',
    warning: 'app-toast--warning',
    success: 'app-toast--success',
  }[severity] ?? '';
}

/**
 * Sort weight, so "most urgent first" is available as a view option without the
 * view knowing the severity names. Higher is more urgent.
 */
export function severityWeight(severity) {
  return { critical: 3, warning: 2, success: 1, info: 0 }[severity] ?? 0;
}

/** "Today 14:32 EST" for today's rows, "2026-07-14 09:05 EST" otherwise. */
export function fmtNotifTs(ts, now = Date.now()) {
  const dateStr = estDateKey(ts);
  if (!dateStr) return '';
  const label = dateStr === estDateKey(now) ? 'Today' : dateStr;
  return `${label} ${estTime(ts)} EST`;
}

/**
 * Group rows into day buckets, preserving the order the server sent them.
 *
 * An array rather than an object, so the caller cannot depend on JS key
 * ordering and a date key can never collide with something off Object.prototype.
 */
export function groupByDay(rows, now = Date.now()) {
  const order = [];
  const buckets = new Map();

  for (const row of rows ?? []) {
    const key = estDateKey(row.created_at);
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key).push(row);
  }

  const today = estDateKey(now);
  const yesterday = estDateKey(now - DAY_MS);

  return order.map(date => ({
    date,
    label: date === today ? 'Today' : date === yesterday ? 'Yesterday' : date,
    items: buckets.get(date),
  }));
}

/**
 * Page arithmetic for the prev/next controls.
 *
 * totalPages floors at 1 so an empty history reads "Page 1 of 1" rather than
 * "Page 1 of 0" — the same correction lib/activity.mjs makes.
 */
export function paginate(offset, total, limit = NOTIF_LIMIT) {
  const safeTotal = Math.max(0, Number(total) || 0);
  return {
    page: Math.floor(offset / limit) + 1,
    totalPages: Math.max(1, Math.ceil(safeTotal / limit)),
    hasPrev: offset > 0,
    hasNext: offset + limit < safeTotal,
    total: safeTotal,
  };
}

/**
 * Query string for GET /api/notifications, omitting anything empty.
 *
 * `types` goes over as a single comma-joined value rather than a repeated
 * param. Both are accepted server-side; one is easier to read in a URL bar, and
 * these filters live in the URL so the view is shareable and survives a refresh.
 */
export function notificationsQuery({ offset = 0, types = [], q = '', from = '', to = '', unread = false, limit = NOTIF_LIMIT } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const list = (types ?? []).filter(Boolean);
  if (list.length) params.set('types', list.join(','));
  if (q) params.set('q', q);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (unread) params.set('unread', '1');
  return params.toString();
}

/** Parse the `types` URL param back into an array. '' means "no filter". */
export function parseTypes(value) {
  return String(value ?? '').split(',').map(t => t.trim()).filter(Boolean);
}

/* ── The preference matrix ─────────────────────────────────────────────────── */

/**
 * Lay a user's stored preferences over the catalogue, flat.
 *
 * Extracted so the Settings rail badge ("17 on") and the switch matrix it
 * summarises resolve a preference the same way. When this logic lived inside
 * groupEvents, anything that needed the flat list — a count, a summary — had to
 * re-implement the defaulting, and the copy that drifted would be the one
 * telling the user what they were receiving.
 *
 * @param {Array} events  From GET /preferences — already filtered to this role.
 * @param {Object} prefs  From GET /preferences — { [type]: { inApp, telegram, isDefault } }.
 */
export function mergePrefs(events, prefs) {
  return (events ?? []).map(event => ({
    ...event,
    // A type with no entry in `prefs` should never happen — the server builds
    // both from the same list — but defaulting to the catalogue's own value
    // means a mismatch renders a sane switch instead of an unchecked one that
    // silently misreports what the user is actually receiving.
    inApp: prefs?.[event.type]?.inApp ?? event.defaults?.inApp ?? false,
    telegram: prefs?.[event.type]?.telegram ?? event.defaults?.telegram ?? false,
    isDefault: prefs?.[event.type]?.isDefault ?? true,
  }));
}

/**
 * Turn the flat catalogue into the grouped rows the preferences screen renders.
 *
 * Group order follows FIRST APPEARANCE in the catalogue, not alphabetical: the
 * server file is deliberately ordered for reading top to bottom, and re-sorting
 * here would throw that away and put "Connection health" above "Performance".
 */
export function groupEvents(events, prefs) {
  const order = [];
  const buckets = new Map();

  // Merged through mergePrefs rather than inline, so the Settings rail badge
  // ("17 on") and the switches it summarises cannot disagree about what is
  // actually on — they now read the same defaulting rules from one place.
  for (const event of mergePrefs(events, prefs)) {
    if (!buckets.has(event.group)) { buckets.set(event.group, []); order.push(event.group); }
    buckets.get(event.group).push(event);
  }

  return order.map(group => ({ group, events: buckets.get(group) }));
}

/**
 * How many types are switched on for a channel, and how many there are — the
 * "4 of 7" counter beside each group heading.
 */
export function channelSummary(events, channel) {
  const list = events ?? [];
  return { on: list.filter(e => e[channel]).length, total: list.length };
}

/**
 * The PUT body for one switch.
 *
 * Only the channel that moved is sent. The server merges it over that type's
 * current effective value, so a single toggle cannot blank the other channel —
 * sending both would make this function responsible for knowing the other one's
 * state, which is exactly the race two open Settings tabs would lose.
 */
export function prefPatch(type, channel, value) {
  return { prefs: { [type]: { [channel]: !!value } } };
}

/**
 * A whole-group toggle: every type in the group, one channel, one request.
 *
 * `value` is applied to all of them rather than each being flipped, so the
 * control reads as "turn this group on/off" rather than "invert this group",
 * which is what a header checkbox is understood to mean.
 */
export function groupPatch(events, channel, value) {
  const prefs = {};
  for (const event of events ?? []) prefs[event.type] = { [channel]: !!value };
  return { prefs };
}

/* ── The detail view ───────────────────────────────────────────────────────── */

// Words that look wrong title-cased. The list is short on purpose — it covers
// what the ~20 emit() call sites actually put in a payload, and a general
// acronym dictionary would start "correcting" campaign names next.
const ACRONYMS = { roas: 'ROAS', cpa: 'CPA', id: 'ID', url: 'URL', bm: 'BM' };

/** `campaignId` → "Campaign ID", `totalPay` → "Total pay", `roas` → "ROAS". */
export function humanKey(key) {
  return String(key ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (ACRONYMS[lower]) return ACRONYMS[lower];
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/** Booleans read as words; everything non-primitive falls back to its JSON. */
export function formatPayloadValue(value) {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/**
 * A notification's `payload` as displayable rows.
 *
 * Every emit() call site stores a flat object of primitives — campaign ids,
 * ROAS figures, the week a payout covers — and none of it is shown anywhere
 * today. It is the part of a notification that answers "which campaign?", so
 * the detail view renders it rather than leaving it write-only in the database.
 *
 * Empty strings, null and undefined are dropped: a labelled blank tells the
 * reader nothing and makes the list longer to scan. `0` and `false` are KEPT —
 * a ROAS of 0 or "is new: No" are both real answers, and filtering them out
 * with a truthiness test is the classic way to lose them.
 */
/**
 * Keys the detail view draws itself: `href` is the button into the app, `items`
 * is the list under it. Leaving them in this list would dump the path and a
 * JSON blob of ads beside that button.
 */
const PAYLOAD_OWN_KEYS = new Set(['href', 'items']);

export function payloadRows(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  return Object.entries(payload)
    .filter(([key, v]) => !PAYLOAD_OWN_KEYS.has(key) && v !== null && v !== undefined && v !== '')
    .map(([key, value]) => ({ key, label: humanKey(key), value: formatPayloadValue(value) }));
}

/**
 * The badge's display value. Anything past 99 becomes "99+" — the exact count
 * stops being useful long before that and a four-digit badge breaks the layout.
 */
export function badgeLabel(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n === 0) return '';
  return n > 99 ? '99+' : String(n);
}
